/**
 * Which admin verdicts a session may be given, and when a reason is required.
 *
 * A DELIBERATE MIRROR of the backend's `SessionVerdictPolicy` (Domain/Sessions). The
 * server is the authority and enforces every rule here independently — this copy exists so
 * the UI never offers a choice the API would refuse, which is worse than not offering it at
 * all. Both files are small and exhaustive, and each names the other; if one changes, the
 * other must.
 *
 * The rules, in full:
 *
 *   1. A run nobody has started takes NO verdict. It has produced no evidence, so there is
 *      nothing for a human to judge, and the UI shows no badge at all rather than an
 *      "NA" that looks like a decision someone made.
 *
 *   2. Approved requires a FINISHED run — In Review, Approved or Rejected. Approving a run
 *      still in flight silently accepts checks that never ran, and afterwards the record
 *      cannot be told apart from an approval of a complete run. The path for accepting an
 *      incomplete run is Exception Requested, which records itself as unusual.
 *
 * Everything else stays open, overrides included: this axis never overwrites the system's
 * conclusion, so the rules' verdict survives whatever an admin decides, which is exactly
 * what makes an override safe to allow.
 */

/**
 * The backend's other rule — WHEN A REASON IS REQUIRED — is not mirrored here as a
 * function, and deliberately so: every dialog that records a verdict makes the admin pick
 * a reason from a preselected radio group, so a note is always sent and the requirement can
 * never be tripped from the UI. `SessionVerdictPolicy.RequiresNote` still enforces it for
 * any other caller, and a refusal is surfaced inline in the dialog. A dead mirror of a rule
 * the UI satisfies structurally would only invite the two to drift.
 */

/** The four settable verdicts, in the order the dialogs list them. */
const VERDICTS = ['Approved', 'Rejected', 'ResubmitRequested', 'ExceptionRequested'] as const;

export type Verdict = (typeof VERDICTS)[number] | 'NotApplicable';

/** The three system statuses that mean "the applicant is done; a human decides now". */
const FINISHED = new Set(['InReview', 'Approved', 'Rejected']);

/**
 * Whether this run can be given an admin verdict at all. False only for Not Started —
 * used to hide the badge and the menu entirely.
 */
export function acceptsVerdict(originalStatus: string): boolean {
  return originalStatus !== 'NotStarted';
}

/** Whether `verdict` may be set on a run whose system status is `originalStatus`. */
export function isVerdictAllowed(originalStatus: string, verdict: Verdict): boolean {
  if (!acceptsVerdict(originalStatus)) return false;
  if (verdict === 'NotApplicable') return true;
  if (verdict === 'Approved') return FINISHED.has(originalStatus);
  return true;
}

/**
 * Why a verdict cannot be set, in words an admin can act on. Null when it can.
 * Shown as the disabled control's tooltip, so it has to name the reason rather than
 * merely refusing.
 */
export function verdictBlockedReason(originalStatus: string): string | null {
  if (!acceptsVerdict(originalStatus)) {
    return "Nothing to review yet — the applicant hasn't started this session.";
  }
  return null;
}
